import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchQuoteDetail } from '../api/quotes';
import type { QuoteDetail } from '../api/types';
import { t } from '../lib/i18n';
import ErrorRetry from './ErrorRetry';
import Modal from './Modal';
import TableSkeleton from './TableSkeleton';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

interface Props {
  sid: number;
  tnum: number;
  onClose: () => void;
}

export default function QuoteDetailModal({ sid, tnum, onClose }: Props) {
  const [data, setData] = useState<QuoteDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setData(null);
    fetchQuoteDetail(sid, tnum)
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [sid, tnum]);

  const stateBadge = (h: QuoteDetail['header']) => {
    if (h.is_invoiced) return <Badge color="emerald">{t.quotes.state.invoiced}</Badge>;
    if (h.is_quote)    return <Badge color="gray">{t.quotes.state.open}</Badge>;
    return <Badge color="amber">{t.quotes.state.converted}</Badge>;
  };

  return (
    <Modal onClose={onClose} ariaLabel={t.quotes.detail.title} role="dialog" panelClassName="max-w-4xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Title>{t.quotes.detail.title}</Title>
          <Text className="text-tremor-content text-sm">Coti #{tnum} · Tienda {sid}</Text>
        </div>
        <Button variant="light" onClick={onClose}>{t.common.close}</Button>
      </div>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && !data && <TableSkeleton rows={6} cols={5} />}

      {data && (
        <>
          <Card>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Info label={t.quotes.cols.cdate}   value={data.header.cdate} />
              <Info label={t.quotes.cols.account} value={`${data.header.anum} · ${data.header.anum_name || '—'}`} />
              <Info label={t.quotes.cols.slid}    value={data.header.slid || '—'} />
              <Info label={t.quotes.cols.state}   value={stateBadge(data.header)} />
              <Info label={t.quotes.cols.subt}    value={fmtMxn.format(data.header.subt_mxn)} />
              <Info label={t.quotes.cols.days}    value={String(data.header.days_open)} />
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Text className="px-4 pt-3 font-semibold">{t.quotes.detail.lines}</Text>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Línea</TableHeaderCell>
                  <TableHeaderCell>Parte</TableHeaderCell>
                  <TableHeaderCell>Descripción</TableHeaderCell>
                  <TableHeaderCell className="text-right">Cant.</TableHeaderCell>
                  <TableHeaderCell className="text-right">Precio</TableHeaderCell>
                  {data.lines.some((ln) => ln.pcost_mxn != null) && (
                    <TableHeaderCell className="text-right">Costo</TableHeaderCell>
                  )}
                  <TableHeaderCell className="text-right">Subtotal</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((ln, i) => (
                  <TableRow key={`${ln.line}-${ln.part}-${i}`}>
                    <TableCell>{ln.line}</TableCell>
                    <TableCell className="font-medium">{ln.part}</TableCell>
                    <TableCell>{ln.pdesc}</TableCell>
                    <TableCell className="text-right">{fmtQty.format(ln.qty)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.sell_mxn)}</TableCell>
                    {data.lines.some((x) => x.pcost_mxn != null) && (
                      <TableCell className="text-right">
                        {ln.pcost_mxn == null ? '—' : fmtMxn.format(ln.pcost_mxn)}
                      </TableCell>
                    )}
                    <TableCell className="text-right">{fmtMxn.format(ln.ext_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong font-semibold mt-0.5">{value}</div>
    </div>
  );
}
